import {
  DASHBOARD_GRAPH_AUCTIONS,
  DASHBOARD_GRAPH_BUYERS,
  DashboardGraphID,
  IDashboardHomeCardMetricKey,
} from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import { IDashboardChartMetric } from '~/src/dash/components/charts/common';
import {
  formatSelectedBidderGroupIntoStringOfIDs,
  formatSelectedByJoiningIDsWithCommas,
  formatSelectedChannelGroupIntoStringOfIDs,
  formatSelectedToStringWithEscapes,
  formatSelectedToUpperCaseStringWithCommasAndEscapes,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { DateRange } from '~/src/dash/utils/dateTimeRanges';
import {
  clearedActiveFilters,
  IActiveFilters,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';

export interface IMobileDashboardHomeCardsConfig {
  groupings: Record<DashboardGraphID, string[]>;
  metrics: Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>>;
}

export interface IMobileDashboardOverviewConfig {
  defaultState: {
    graphID: DashboardGraphID;
    isCompareEnabled: boolean;
    grouping: number;
    displayedMetricKeys: IDashboardHomeCardMetricKey[];
    activeChartMetrics: IDashboardChartMetric[];
    dateRange: DateRange;
    activeFilters: IActiveFilters;
  };
  orderedFilterKeys: string[];
  filters: ICustomAPIFilterConfig;
  cards: IMobileDashboardHomeCardsConfig;
}

export const mobileDashboardOverviewConfig: IMobileDashboardOverviewConfig = {
  defaultState: {
    graphID: DASHBOARD_GRAPH_AUCTIONS,
    isCompareEnabled: true,
    grouping: EDateGroup.ByDay,
    dateRange: 'Last 7 Days',
    displayedMetricKeys: [
      'Revenue',
      'Impressions',
      'CPM',
      'EndpointRequests',
      'RenderRate',
      'PodFillRate',
    ],
    activeChartMetrics: [
      {
        key: 'Revenue',
        label: 'Revenue',
        chartColorIndex: 0,
        chartType: 'bar',
        valueType: 'currency',
      },
    ],
    activeFilters: clearedActiveFilters,
  },
  orderedFilterKeys: [
    'Bidders',
    'Filtered Demand Sources',
    'Bidder Groups',
    'Channels',
    'Channel Groups',
    'Device Types',
  ],
  filters: {
    Bidders: {
      queryKey: 'bidder_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 100,
      displayID: true,
      category: 'demand',
    },
    'Filtered Demand Sources': {
      queryKey: 'biddercodes',
      formatRequestValue: formatSelectedToStringWithEscapes,
      limit: 20,
      category: 'demand',
    },
    'Bidder Groups': {
      queryKey: 'bidder_ids',
      formatRequestValue: formatSelectedBidderGroupIntoStringOfIDs,
      limit: 1,
      displayID: true,
      category: 'demand',
    },
    Channels: {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedByJoiningIDsWithCommas,
      limit: 20,
      displayID: true,
      category: 'supply',
    },
    'Channel Groups': {
      queryKey: 'site_ids',
      formatRequestValue: formatSelectedChannelGroupIntoStringOfIDs,
      limit: 1,
      displayID: true,
      category: 'supply',
    },
    'Device Types': {
      queryKey: 'devicetypes',
      formatRequestValue: formatSelectedToUpperCaseStringWithCommasAndEscapes,
      category: 'device',
    },
  } as ICustomAPIFilterConfig,
  cards: {
    groupings: {
      [DASHBOARD_GRAPH_AUCTIONS]: [
        'Revenue',
        'Impressions',
        'CPM',
        'EndpointRequests',
        'RenderRate',
        'RenderedPodFillRate',
        'PodFillRate',
        'Auctions',
        'CPMPerSecond',
        'EndpointRequestsECPM',
        'FillRate',
        'PodClearRate',
        'PaidPodClearRate',
      ],
      [DASHBOARD_GRAPH_BUYERS]: [
        'Revenue',
        'Impressions',
        'CPM',
        'WinningBids',
        'RenderRate',
        'BidRequests',
        'BidResponses',
        'BidResponseRate',
        'BidTimeoutRate',
        'CPMPerSecond',
      ],
    },
    metrics: {
      Revenue: {
        key: 'Revenue',
        label: 'Revenue',
        responseKey: 'Revenue',
        trendKey: 'RevenueTrend',
        valueType: 'currency',
        chartType: 'bar',
      },
      Impressions: {
        key: 'Impressions',
        label: 'Impressions',
        responseKey: 'Impressions',
        trendKey: 'ImpressionsTrend',
        valueType: 'integer',
        chartType: 'bar',
      },
      CPM: {
        key: 'CPM',
        label: 'CPM',
        responseKey: 'CPM',
        trendKey: 'CPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
      },
      CPMPerSecond: {
        key: 'CPMPerSecond',
        label: 'CPM Per Second',
        responseKey: 'CPMPerSecond',
        trendKey: 'CPMPerSecondTrend',
        valueType: 'currency_rate',
        chartType: 'line',
      },
      RenderRate: {
        key: 'RenderRate',
        label: 'Render Rate',
        responseKey: 'RenderRate',
        trendKey: 'RenderRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      EndpointRequests: {
        key: 'EndpointRequests',
        label: 'Endpoint Requests',
        responseKey: 'EndpointRequests',
        trendKey: 'EndpointRequestsTrend',
        valueType: 'integer',
        chartType: 'bar',
      },
      EndpointRequestsECPM: {
        key: 'EndpointRequestsECPM',
        label: 'Endpoint Requests eCPM',
        responseKey: 'EndpointRequestsECPM',
        trendKey: 'EndpointRequestsECPMTrend',
        valueType: 'currency_rate',
        chartType: 'line',
      },
      Auctions: {
        key: 'Auctions',
        label: 'Auctions',
        responseKey: 'Auctions',
        trendKey: 'AuctionsTrend',
        valueType: 'integer',
        chartType: 'bar',
      },
      RenderedPodFillRate: {
        key: 'RenderedPodFillRate',
        label: 'Rendered Pod Fill Rate',
        responseKey: 'RenderedPodFillRate',
        trendKey: 'RenderedPodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      PodFillRate: {
        key: 'PodFillRate',
        label: 'Pod Fill Rate',
        responseKey: 'PodFillRate',
        trendKey: 'PodFillRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      PodClearRate: {
        key: 'PodClearRate',
        label: 'Pod Clear Rate',
        responseKey: 'PodClearRate',
        trendKey: 'PodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      PaidPodClearRate: {
        key: 'PaidPodClearRate',
        label: 'Paid Pod Clear Rate',
        responseKey: 'PaidPodClearRate',
        trendKey: 'PaidPodClearRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      FillRate: {
        key: 'FillRate',
        label: 'Fill Rate',
        responseKey: 'FillRate',
        trendKey: 'FillRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      BidRequests: {
        key: 'BidRequests',
        label: 'Bid Requests',
        responseKey: 'BidRequests',
        trendKey: 'BidRequestsTrend',
        valueType: 'integer',
        chartType: 'bar',
      },
      BidResponses: {
        key: 'BidResponses',
        label: 'Bid Responses',
        responseKey: 'BidResponses',
        trendKey: 'BidResponsesTrend',
        valueType: 'integer',
        chartType: 'bar',
      },
      WinningBids: {
        key: 'WinningBids',
        label: 'Winning Bids',
        responseKey: 'WinningBids',
        trendKey: 'WinningBidsTrend',
        valueType: 'integer',
        chartType: 'bar',
      },
      BidResponseRate: {
        key: 'BidResponseRate',
        label: 'Bid Response Rate',
        responseKey: 'BidResponseRate',
        trendKey: 'BidResponseRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
      BidTimeoutRate: {
        isInverseTrend: true,
        key: 'BidTimeoutRate',
        label: 'Bid Timeout Rate',
        responseKey: 'BidTimeoutRate',
        trendKey: 'BidTimeoutRateTrend',
        valueType: 'percent',
        chartType: 'line',
      },
    },
  },
};
